import {
  PixelConfig,
  PixelEvent,
  useEventPixels,
} from "./useEventPixels";

export type { PixelEvent } from "./useEventPixels";

export type PixelMetadata = {
  content_ids?: Array<string | number>;
  content_name?: string;
  content_type?: string;
  contents?: Array<{
    id: string | number;
    quantity: number;
    item_price?: number;
  }>;
  currency?: string;
  value?: number;
  transaction_id?: string;
};

function toTiktokMetadata(metadata?: PixelMetadata) {
  if (!metadata) {
    return undefined;
  }

  return {
    content_ids: metadata.content_ids,
    content_type: metadata.content_type,
    currency: metadata.currency,
    description: metadata.content_name,
    quantity: metadata.contents?.reduce(
      (total, content) => total + content.quantity,
      0
    ),
    value: metadata.value,
  };
}

function isEventEnabled(pixel: PixelConfig<PixelEvent>, eventName: PixelEvent) {
  return pixel.enabled_events.includes(eventName);
}

export function usePixelTracking<EventT extends PixelEvent>({
  eventSlug,
}: {
  eventSlug?: string;
}) {
  const { data, isSuccess } = useEventPixels(eventSlug);

  const track = ({
    eventName,
    metadata,
  }: {
    eventName: EventT;
    metadata?: PixelMetadata;
  }) => {
    try {
      // Docs: [https://developers.facebook.com/docs/meta-pixel/reference#standard-events]
      for (const pixel of data?.facebook ?? []) {
        if (
          isEventEnabled(pixel, eventName) &&
          typeof window.fbq != "undefined"
        ) {
          window.fbq("set", "autoConfig", false, pixel.code);
          window.fbq("init", pixel.code);
          window.fbq("trackSingle", pixel.code, eventName, metadata);
        }
      }

      // Docs: [https://ads.tiktok.com/marketing_api/docs]
      for (const pixel of data?.tiktok ?? []) {
        if (
          isEventEnabled(pixel, eventName) &&
          typeof window.ttq != "undefined"
        ) {
          window.ttq.load(pixel.code);
          window.ttq.track(eventName, toTiktokMetadata(metadata));
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return {
    track,
    isSuccess,
  };
}
